import React from 'react';
import DisputeStatisticsCards from './DisputeStatisticsCards';

// Component for displaying dispute type breakdown
const CaseTypeBreakdown = ({ caseTypes = {} }) => {
  const types = [
    { name: 'Settlement',  value: caseTypes?.settlement  || 0, color: 'bg-blue-500' },
    { name: 'Arbitration', value: caseTypes?.arbitration || 0, color: 'bg-green-500' },
    { name: 'Mediation',   value: caseTypes?.mediation   || 0, color: 'bg-yellow-500' }
  ];

  const total = types.reduce((sum, type) => sum + type.value, 0) || 1; // Prevent division by zero

  return (
    <div className="bg-white p-5 rounded-xl shadow-sm h-full">
      <h3 className="text-gray-800 font-semibold mb-4">Case Types Breakdown</h3>
      <div className="space-y-4">
        {types.map((type, index) => (
          <div key={index}>
            <div className="flex justify-between mb-1">
              <span className="text-sm text-gray-600">{type.name}</span>
              <span className="text-sm font-medium">{type.value} ({Math.round((type.value / total) * 100)}%)</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`${type.color} h-2 rounded-full`}
                style={{ width: `${Math.max((type.value / total) * 100, 1)}%` }}
              ></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Component for displaying incident types
const IncidentTypesTable = ({ incidentTypes = [] }) => {
  return (
    <div className="bg-white p-5 rounded-xl shadow-sm h-full">
      <h3 className="text-gray-800 font-semibold mb-4">Incident Types</h3>
      {incidentTypes.length === 0 ? (
        <p className="text-gray-500 text-sm">No incident types recorded</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th className="text-center py-2 px-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Count</th>
              </tr>
            </thead>
            <tbody>
              {incidentTypes.map((type, index) => (
                <tr key={index} className="border-b border-gray-100">
                  <td className="py-2 px-3 text-sm text-gray-800">{type.incident_type || 'Unknown'}</td>
                  <td className="py-2 px-3 text-sm text-center font-medium text-gray-800">{type.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Component for displaying monthly trends
const MonthlyTrend = ({ monthlyTrend = [] }) => {
  const maxCount = Math.max(...monthlyTrend.map(item => item.count), 1);

  return (
    <div className="bg-white p-5 rounded-xl shadow-sm h-full">
      <h3 className="text-gray-800 font-semibold mb-4">Monthly Case Trends</h3>
      <div className="flex items-end h-40 mt-2 space-x-2">
        {monthlyTrend.map((item, index) => (
          <div key={index} className="flex flex-col items-center flex-1">
            <div className="relative w-full flex justify-center">
              <div
                className="w-full max-w-[25px] bg-blue-500 rounded-t-sm"
                style={{
                  height: `${Math.max((item.count / maxCount) * 100, item.count > 0 ? 10 : 0)}%`,
                  opacity: item.count > 0 ? 1 : 0.3
                }}
              ></div>
              {item.count > 0 && (
                <span className="absolute -top-6 text-xs font-medium text-blue-700">
                  {item.count}
                </span>
              )}
            </div>
            <span className="text-xs mt-2 text-gray-600">{item.month}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Component for displaying resolution metrics
const ResolutionMetrics = ({ statistics = {} }) => {
  return (
    <div className="bg-white p-5 rounded-xl shadow-sm h-full">
      <h3 className="text-gray-800 font-semibold mb-4">Resolution Metrics</h3>
      <div className="grid grid-cols-2 gap-4">
        <div className="text-center p-3 bg-blue-50 rounded-lg">
          <p className="text-sm text-gray-600 mb-1">Resolution Rate</p>
          <p className="text-2xl font-bold text-blue-600">{statistics.resolution_rate || 0}%</p>
        </div>
        <div className="text-center p-3 bg-green-50 rounded-lg">
          <p className="text-sm text-gray-600 mb-1">Avg. Resolution Time</p>
          <p className="text-2xl font-bold text-green-600">{statistics.avg_resolution_days || 0} days</p>
        </div>
        <div className="text-center p-3 bg-purple-50 rounded-lg">
          <p className="text-sm text-gray-600 mb-1">Successful Mediations</p>
          <p className="text-2xl font-bold text-purple-600">{statistics.successful_mediations || 0}</p>
        </div>
        <div className="text-center p-3 bg-amber-50 rounded-lg">
          <p className="text-sm text-gray-600 mb-1">New Cases (Month)</p>
          <p className="text-2xl font-bold text-amber-600">{statistics.this_month?.new_cases || 0}</p>
        </div>
      </div>
    </div>
  );
};

// Main dashboard component
const DisputeDashboard = ({ statistics = {} }) => {
  return (
    <div className="space-y-6">
      {/* Main statistics cards */}
      <DisputeStatisticsCards statistics={statistics} />

      {/* Detailed analytics section */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="lg:col-span-1">
          <CaseTypeBreakdown caseTypes={statistics.case_types} />
        </div>
        <div className="lg:col-span-3">
          <MonthlyTrend monthlyTrend={statistics.monthly_trend} />
        </div>
        <div className="lg:col-span-2">
          <ResolutionMetrics statistics={statistics} />
        </div>
        <div className="lg:col-span-2">
          <IncidentTypesTable incidentTypes={statistics.incident_types} />
        </div>
      </div>
    </div>
  );
};

export default React.memo(DisputeDashboard);
